/**
 * Bar Intelligence's pattern stats — depth, duration, trigger, target, stop,
 * reward to risk, since the breakout, target hit.
 *
 * `docs/plans/bar-intelligence.md`, "Pattern stats". The detectors send each
 * shape's **facts** (`GeometryShape.stats`: where it starts and ends, the prices
 * it is measured by); every number here is a measurement against the chart's
 * own bars, so it moves with the current bar and the High/Close setting.
 */

import type { GeometryPayload, GeometryShape } from "@qufolio/transport";
import type { Plot } from "@qufolio/model";
import type { BiMeasure } from "./bar-intelligence";

/** The chart's real bars, as columns. `t` epoch seconds ascending. */
export interface ChartBars {
  t: readonly number[];
  /** The open, for the inside setup's gap fill; absent reads as no gap. */
  o?: readonly number[];
  h: readonly number[];
  l: readonly number[];
  c: readonly number[];
}

export interface PatternRow {
  id: string;
  name: string;
  bias: "bullish" | "bearish";
  status: string;
  /** `forming`, `confirmed 3d`, `target ✓`, … */
  statusText: string;
  /** Unix seconds of the pattern's first bar. */
  start?: number;
  depthPct?: number;
  bars?: number;
  handle?: { depthPct?: number; bars: number };
  trigger?: number;
  toTriggerPct?: number;
  target?: number;
  toTargetPct?: number;
  stop?: number;
  rr?: number;
  sinceBreakout?: { bars: number; pct: number };
  targetHit?: { hit: boolean; bars?: number };
  touches?: number;
}

/** The pattern plots a chart draws — every `geometry` plot but Bar Intelligence's own. */
const PATTERN_KINDS = new Set(["cupHandle", "headShoulders", "darvasBox", "flatBase", "trendline"]);

function nameOf(shape: GeometryShape & { inverse?: boolean }): string {
  switch (shape.kind) {
    case "cupHandle":
      return shape.inverse ? "Inv cup & handle" : "Cup & handle";
    case "headShoulders":
      return shape.inverse ? "Inv H&S" : "H&S";
    case "darvasBox":
      return "Darvas box";
    case "flatBase":
      return "Flat base";
    case "trendline": {
      const seg = shape.primitives.find((p) => p.t === "segment");
      return seg && seg.t === "segment" && seg.role === "support" ? "TL support" : "TL resistance";
    }
    default:
      return shape.kind;
  }
}

/** Index of the bar at or before `t`, or `undefined` before the first. */
export function indexAtOrBefore(times: readonly number[], t: number): number | undefined {
  let lo = 0;
  let hi = times.length - 1;
  if (hi < 0 || t < times[0]!) return undefined;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (times[mid]! <= t) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

const pct = (from: number, to: number) => ((to - from) / from) * 100;

/**
 * One row per pattern shape the chart's pattern plots hold, newest first.
 *
 * The current bar is the last of `bars`; a bullish pattern is measured from its
 * High (Close), a bearish one from its Low (Close) — the same flip the levels
 * make (Q9).
 */
export function patternRows(
  geometry: GeometryPayload | null | undefined,
  plots: readonly Plot[] | undefined,
  bars: ChartBars,
  measure: BiMeasure,
): PatternRow[] {
  if (!geometry || !plots || bars.t.length === 0) return [];
  const last = bars.t.length - 1;
  const rows: { row: PatternRow; end: number }[] = [];
  for (const plot of plots) {
    if (plot.type !== "geometry" || plot.hidden || !plot.outputAlias) continue;
    for (const shape of geometry.sets[plot.outputAlias] ?? []) {
      if (!PATTERN_KINDS.has(shape.kind)) continue;
      const row = patternRow(shape, bars, last, measure);
      rows.push({ row, end: shape.stats?.end ?? shape.breakAt ?? 0 });
    }
  }
  return rows.sort((a, b) => b.end - a.end).map((r) => r.row);
}

function patternRow(shape: GeometryShape, bars: ChartBars, last: number, measure: BiMeasure): PatternRow {
  const bias = shape.bias ?? "bullish";
  const bull = bias === "bullish";
  const m = measure === "close" ? bars.c[last]! : bull ? bars.h[last]! : bars.l[last]!;
  const row: PatternRow = {
    id: shape.id,
    name: nameOf(shape as GeometryShape & { inverse?: boolean }),
    bias,
    status: shape.status,
    statusText: shape.status,
  };
  const st = shape.stats;

  if (shape.kind === "trendline") {
    const seg = shape.primitives.find((p) => p.t === "segment");
    if (seg && seg.t === "segment") {
      const a = indexAtOrBefore(bars.t, seg.a.t);
      const b = indexAtOrBefore(bars.t, (seg.via ?? seg.b).t);
      if (a !== undefined && b !== undefined) row.bars = b - a;
      row.start = seg.a.t;
      row.touches = seg.touches?.length;
    }
    if (shape.trigger) {
      const at = indexAtOrBefore(bars.t, shape.trigger.t);
      const k = at === undefined ? 0 : last - at;
      row.trigger = shape.trigger.price + shape.trigger.slopePerBar * k;
      row.toTriggerPct = pct(row.trigger, m);
    }
  } else if (st) {
    if (st.top !== undefined && st.bottom !== undefined && st.top !== 0) {
      row.depthPct = ((st.top - st.bottom) / st.top) * 100;
    }
    row.bars = st.bars;
    row.start = st.start;
    if (st.handle) {
      const h = st.handle;
      row.handle = {
        bars: h.bars,
        depthPct: h.top !== undefined && h.bottom !== undefined && h.top !== 0 ? ((h.top - h.bottom) / h.top) * 100 : undefined,
      };
    }
    row.trigger = st.trigger;
    row.target = st.target;
    row.stop = st.stop;
    if (st.trigger !== undefined) row.toTriggerPct = pct(st.trigger, m);
    if (st.target !== undefined) row.toTargetPct = pct(m, st.target);
    if (st.trigger !== undefined && st.target !== undefined && st.stop !== undefined) {
      const risk = Math.abs(st.trigger - st.stop);
      if (risk > 0) row.rr = Math.abs(st.target - st.trigger) / risk;
    }
  }

  if (shape.breakAt !== undefined) {
    const at = indexAtOrBefore(bars.t, shape.breakAt);
    if (at !== undefined) {
      row.sinceBreakout = { bars: last - at, pct: pct(bars.c[at]!, bars.c[last]!) };
      row.statusText = `${shape.status} ${last - at}`;
      const target = row.target;
      if (target !== undefined && shape.status === "confirmed") {
        let hit: number | undefined;
        for (let j = at + 1; j <= last; j++) {
          if (bull ? bars.h[j]! >= target : bars.l[j]! <= target) {
            hit = j - at;
            break;
          }
        }
        row.targetHit = hit === undefined ? { hit: false } : { hit: true, bars: hit };
        if (hit !== undefined) row.statusText = "target ✓";
      }
    }
  }
  return row;
}
